/**
 * Authentication composable.
 */

import { computed, ref } from "vue";
import { authAPI, userAPI } from "@/services/api";
import router from "@/router";
import type {
  ChangePasswordRequest,
  LoginRequest,
  RegisterRequest,
  User,
} from "@/types";
import { extractErrorMessage } from "@/utils/error";

const user = ref<User | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
let hasInitializedUser = false;
let fetchCurrentUserPromise: Promise<User | null> | null = null;

export function useAuth() {
  const isAuthenticated = computed(() => !!user.value);
  const username = computed(() => user.value?.username || "");
  const email = computed(() => user.value?.email || "");

  const initUser = () => {
    if (hasInitializedUser) {
      return user.value;
    }

    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        user.value = JSON.parse(userStr);
      } catch (err) {
        console.error("Failed to parse user info:", err);
        localStorage.removeItem("user");
      }
    }

    hasInitializedUser = true;
    return user.value;
  };

  const fetchCurrentUser = async (options?: { force?: boolean }) => {
    const force = options?.force === true;
    if (!force) {
      initUser();
      if (user.value) {
        return user.value;
      }
      if (fetchCurrentUserPromise) {
        return fetchCurrentUserPromise;
      }
    }

    fetchCurrentUserPromise = (async () => {
      try {
        isLoading.value = true;
        error.value = null;

        const response = await userAPI.getCurrentUser();
        user.value = response.data;
        localStorage.setItem("user", JSON.stringify(user.value));
        hasInitializedUser = true;

        return user.value;
      } catch (err) {
        error.value = extractErrorMessage(err);
        throw err;
      } finally {
        isLoading.value = false;
        fetchCurrentUserPromise = null;
      }
    })();

    return fetchCurrentUserPromise;
  };

  const login = async (data: LoginRequest) => {
    try {
      isLoading.value = true;
      error.value = null;

      await authAPI.login(data);
      await fetchCurrentUser();

      return user.value;
    } catch (err) {
      error.value = extractErrorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      isLoading.value = true;
      error.value = null;

      await authAPI.register(data);
      await login({
        username: data.email,
        password: data.password,
      });

      return user.value;
    } catch (err) {
      error.value = extractErrorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      await authAPI.logout();
    } catch (err) {
      console.error("Logout failed:", err);
      error.value = extractErrorMessage(err);
    } finally {
      localStorage.removeItem("user");
      user.value = null;
      hasInitializedUser = false;
      isLoading.value = false;

      router.push("/login");
    }
  };

  const updateUser = async (data: { username?: string; email?: string }) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await userAPI.updateCurrentUser(data);
      user.value = response.data;
      localStorage.setItem("user", JSON.stringify(user.value));

      return user.value;
    } catch (err) {
      error.value = extractErrorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const changePassword = async (data: ChangePasswordRequest) => {
    try {
      isLoading.value = true;
      error.value = null;

      await userAPI.changePassword(data);
    } catch (err) {
      error.value = extractErrorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    username,
    email,
    initUser,
    fetchCurrentUser,
    login,
    register,
    logout,
    updateUser,
    changePassword,
  };
}
